import React, { useEffect, useState } from "react";
import Pagination from "../reusable/Pagination";
import Modal from "../Modal";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUserInformations } from "../../features/authSlice";
import { deletePayment, selectPaymentDeleteStatus, selectPaymentDeleteError } from "../../features/payments/paymentSlice";
import { hasPermission, showToast } from "../../utils/utils";
import Loader from "../Live/Loader";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { FiEdit, FiRefreshCw, FiCheck, FiClock } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { API } from "../../shared/api/Api";
import axios from "axios";
import VerifiedIcon from "../../assets/svg/VerifiedIcon.svg";
import PendingIcon from "../../assets/svg/PendingIcon.svg";
import Refund from "../../assets/svg/SidebarIcons/refunds-01.svg";
import Close from "../../assets/svg/CloseButton.svg";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { saveAs } from 'file-saver';
import { ReactComponent as ExcelIcon } from '../../assets/svg/Excel.svg';
import { motion } from "framer-motion";

/**
 * PaymentTable Component
 * Refactored to use navigation instead of modals (except refund modal)
 */
const PaymentTable = ({
  payments,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  coursePayments = false,
  courseData = null,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const deleteStatus = useSelector(selectPaymentDeleteStatus);
  const deleteError = useSelector(selectPaymentDeleteError);

  const [isModalRefundOpen, setIsModalRefundOpen] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundData, setRefundData] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null); // Track which payment is being updated

  // Handle payment status change (verify/pending toggle)
  const handleStatusChange = async (paymentId, currentStatus) => {
    const newStatus = currentStatus === 'pending' ? 'verified' : 'pending';

    // Confirm if changing from verified to pending
    if (currentStatus === 'verified') {
      if (!window.confirm("Are you sure you want to change this payment back to pending?")) {
        return;
      }
    }

    setUpdatingStatusId(paymentId);
    try {
      const { data } = await axios.put(
        `${API.Payments.app_courses_payments_changeStatusForPayment}/${paymentId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast(
          "Success",
          `Payment ${newStatus === 'verified' ? 'verified' : 'set to pending'} successfully`,
          "success"
        );
        if (setReFetchData) {
          setReFetchData(true);
        }
      } else {
        showToast("Error", data.message || "Failed to update payment status", "error");
      }
    } catch (error) {
      console.error("Status update error:", error);
      showToast(
        "Error",
        error.response?.data?.message || "Failed to update payment status",
        "error"
      );
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Handle delete with confirmation
  const handleDelete = async (paymentId) => {
    if (window.confirm("Are you sure you want to delete this payment? There is no going back!!")) {
      const result = await dispatch(deletePayment({ token: user.token, paymentId }));

      if (deletePayment.fulfilled.match(result)) {
        showToast('Success', 'Payment deleted successfully', 'success');
        if (setReFetchData) {
          setReFetchData(true);
        }
      } else if (deletePayment.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete payment', 'error');
      }
    }
  };

  // Handle delete errors
  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);

  const handleMethodPreview = (method) => {
    switch (method) {
      case "haram_transfer":
        return "Haram Transfer";
      case "bank_transfer":
        return "Bank Transfer";
      case "cash":
        return "Cash On Site";
      default:
        return method;
    }
  };

  const handleRefundModal = (payment_id) => {
    setIsModalRefundOpen(true);
    setRefundData({
      ...refundData,
      payment_id: payment_id,
    });
  };

  const handleCloseRefundModal = () => {
    setIsModalRefundOpen(false);
    setRefundData(null);
  };

  const handleAddRefund = async () => {
    setRefundLoading(true);
    try {
      const { data } = await axios.post(
        API.Refunds.app_courses_payments_refund_add,
        refundData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (data.status === 201 && data.errorCode === 0) {
        showToast("Refund Payment", data.message, "success");
        handleCloseRefundModal();
        if (setReFetchData) {
          setReFetchData(true);
        }
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Error",
        error.response?.data?.data?.reason?.[0] || "There is an error, please try again",
        "error"
      );
    } finally {
      setRefundLoading(false);
    }
  };

  // Excel export handler
  const exportToExcel = async () => {
    try {
      const response = await axios.get(API.Payments.app_courses_payments_export_to_excel, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      let filename;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition.split('filename=')[1].trim().replace(/['"]/g, '');
      } else {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        const formatted = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        filename = `payments_${formatted}.xlsx`;
      }
      saveAs(response.data, filename);
      showToast('Success', 'Payments exported successfully', 'success');
    } catch (error) {
      showToast(
        'Export Failed',
        error?.response?.data?.message || 'Could not export payments. Please try again.',
        'error'
      );
    }
  };

  const columns = [
    { id: "student_name", label: "Student Name", sortable: false },
    { id: "payment_type", label: "Payment Type", sortable: false },
    { id: "course_title", label: "Course Title", sortable: false },
    { id: "method", label: "Payment Method", sortable: false },
    { id: "status", label: "Current Status", sortable: false },
    { id: "created_at", label: "Created At", sortable: true },
  ];

  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;
    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC" ? "text-primary" : "text-gray-400"} -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC" ? "text-primary" : "text-gray-400"} -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const getDropdownActions = (id) => {
    const actions = [];

    if (hasPermission(user, "view_payment")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/payments/${id}`),
      });
    }

    if (hasPermission(user, "update_payment")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/payments/${id}/edit`),
      });
    }

    if (hasPermission(user, "delete_payment")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(id),
      });
    }

    return actions;
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              {coursePayments
                ? `${courseData?.course?.title || courseData?.title || ""} • Payments`
                : "Payments"}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Track student payments, statuses, and manage refunds.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasPermission(user, "create_payment") && !coursePayments && (
              <button
                type="button"
                onClick={() => navigate("/dashboard/payments/create")}
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Add Payment"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New Payment</span>
              </button>
            )}
            {!coursePayments && hasPermission(user, "export_payment") && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center justify-center gap-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="Export to Excel"
              >
                <ExcelIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setReFetchData && setReFetchData(true)}
              className="inline-flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Header: Course title & Search */}
        <div className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-gray-100 pt-4 gap-3">
          <span className="text-sm sm:text-base font-medium text-gray-800">
            {coursePayments
              ? courseData?.course?.title || courseData?.title || "Course payments"
              : "All payments"}
          </span>
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search payments..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                />
              </svg>
            </span>
          </div>
        </div>

      {/* Table Container */}
      <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="w-full overflow-x-auto">
          <div className="min-w-[960px]">
        {loader && (
          <div className="flex justify-center items-center py-6">
            <Loader />
          </div>
        )}
        <table className="w-full table-auto">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${
                    currentOrderBy === column.id ? "text-primary" : "text-gray-500"
                  } border-b border-gray-100 ${
                    column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                  }`}
                  onClick={
                    column.sortable ? () => handleSortClick(column.id) : undefined
                  }
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {column.sortable && renderSortIndicator(column.id)}
                  </div>
                </th>
              ))}
              {!coursePayments && (
                <th className="px-4 sm:px-6 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  {(hasPermission(user, "create_refund") ||
                    hasPermission(user, "view_payment") ||
                    hasPermission(user, "view_refund")) && "Refund Payment"}
                </th>
              )}
              {(hasPermission(user, "update_payment") ||
                hasPermission(user, "delete_payment") ||
                hasPermission(user, "view_payment")) &&
                !coursePayments && (
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                    Actions
                  </th>
                )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payments.length === 0 && !loader ? (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="px-4 sm:px-6 py-10 text-center text-gray-500"
                >
                  No payments found
                </td>
              </tr>
            ) : (
              payments.map(
                (
                  {
                    student_name,
                    payment_type,
                    payment_for_title,
                    course_title,
                    method,
                    status,
                    created_at,
                    id,
                    has_refund,
                  },
                  index
                ) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                  >
                    <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">
                      {student_name}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700 capitalize">
                      {payment_type === "placement_test" ? "Placement Test" : "Course"}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">{payment_for_title || course_title}</td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {handleMethodPreview(method)}
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex flex-row items-center space-x-3">
                        {/* Status Toggle Switch - Only show if user has permission and not in coursePayments view */}
                        {hasPermission(user, "update_payment") && !coursePayments ? (
                          <div className="flex items-center space-x-2">
                            <motion.button
                              type="button"
                              role="switch"
                              aria-checked={status === 'verified'}
                              disabled={updatingStatusId === id}
                              onClick={() => handleStatusChange(id, status)}
                              className={`
                                relative inline-flex h-7 w-14 items-center rounded-full
                                transition-colors duration-300 ease-in-out
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                disabled:opacity-50 disabled:cursor-not-allowed
                                ${status === 'verified'
                                  ? 'bg-green-500 hover:bg-green-600'
                                  : 'bg-gray-300 hover:bg-gray-400'
                                }
                              `}
                              whileTap={{ scale: 0.95 }}
                            >
                              {/* Loading spinner when updating */}
                              {updatingStatusId === id ? (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              ) : (
                                <>
                                  {/* Toggle circle */}
                                  <motion.span
                                    className={`
                                      inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-md
                                      transform transition-transform duration-300 ease-in-out
                                    `}
                                    animate={{ x: status === 'verified' ? 30 : 4 }}
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                  >
                                    {status === 'verified' ? (
                                      <FiCheck className="w-3 h-3 text-green-500" />
                                    ) : (
                                      <FiClock className="w-3 h-3 text-gray-400" />
                                    )}
                                  </motion.span>
                                </>
                              )}
                            </motion.button>

                            {/* Status text label */}
                            <span
                              className={`text-sm font-medium capitalize ${
                                status === "verified"
                                  ? "text-green-600"
                                  : "text-yellow-600"
                              }`}
                            >
                              {status}
                            </span>
                          </div>
                        ) : (
                          /* Read-only status display */
                          <span
                            className={`text-sm font-medium capitalize ${
                              status === "verified" ? "text-green-600" : "text-yellow-600"
                            }`}
                          >
                            {status}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">{created_at}</td>
                    {!coursePayments && (
                      <td className="px-4 sm:px-6 py-4">
                        {!has_refund
                          ? hasPermission(user, "create_refund") && (
                            <div
                              className="flex items-center flex-col sl:flex-row space-x-2 cursor-pointer"
                              onClick={() => handleRefundModal(id)}
                            >
                              <img
                                className="w-10 h-10"
                                src={Refund}
                                alt="Refund"
                              />
                              <span className="flex items-center text-[10px] md:text-[13px] text-gray-700">
                                Click to Refund
                              </span>
                            </div>
                          )
                          : (hasPermission(user, "view_payment") ||
                            hasPermission(user, "view_refund")) && (
                            <span className="text-[10px] md:text-[13px] text-gray-500">
                              Previous refund exists
                            </span>
                          )}
                      </td>
                    )}
                    {(hasPermission(user, "update_payment") ||
                      hasPermission(user, "delete_payment") ||
                      hasPermission(user, "view_payment")) &&
                      !coursePayments && (
                        <td className="px-4 sm:px-6 py-4 text-right">
                          <ActionsDropdown actions={getDropdownActions(id)} />
                        </td>
                      )}
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-4">
        <p className="hidden sm:block text-xs text-gray-500">
          Showing page <span className="font-semibold">{currentPage}</span> of{" "}
          <span className="font-semibold">{totalPages}</span>
        </p>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      {/* Refund Modal (kept for special functionality) */}
      <Modal
        isOpen={isModalRefundOpen}
        onClose={handleCloseRefundModal}
        width="max-w-2xl"
        withHeader={false}
        withButtons={false}
      >
        <div>
          <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 capitalize">
              Add New Refund
            </h3>
            <button
              onClick={handleCloseRefundModal}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            >
              <img className="w-6 h-6 md:w-8 md:h-8" src={Close} alt="Close" />
            </button>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason To Refund:
          </label>
          <textarea
            name="refund"
            className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-y-auto"
            placeholder="Type refund reason"
            onChange={(e) =>
              setRefundData({ ...refundData, reason: e.target.value })
            }
            value={refundData?.reason || ''}
          />

          <div className="flex justify-end mt-6">
            <button
              className="w-full sm:w-auto px-6 py-3 text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={refundLoading}
              onClick={handleAddRefund}
            >
              {refundLoading ? <Loader /> : "Add"}
            </button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default PaymentTable;
